import { StatusPill } from "@/components/ui/StatusPill";
import { fmtDateTime, fmtElapsed } from "@/lib/format";
import { isOnRunnerStatus, jobStatusLabel } from "@/lib/jobStatus";
import type { JobStatus } from "@/lib/mockData";
import { didRun, runSeconds } from "@/lib/usage";

import { isFinished, type JobDetail } from "./jobApi";
import { Panel } from "./parts";

/**
 * `apiStatus` tells apart the API statuses that share a colour: a job
 * waiting for a person vs. one a supervisor is controlling, and a
 * cancelled job vs. a failed one.
 */
export function JobStatusPill({ status, apiStatus }: { status: JobStatus; apiStatus?: string }) {
  return <StatusPill status={status} label={jobStatusLabel(status, apiStatus)} />;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-3">{label}</dt>
      <dd className="min-w-0 break-words text-ink-1">{children}</dd>
    </>
  );
}

/** Status, workflow, entity, who queued it, the three timestamps and the real duration. */
export function JobSummary({ job }: { job: JobDetail }) {
  const ran = didRun(job);
  const duration = ran ? fmtElapsed(runSeconds(job)) : "—";

  return (
    <Panel title="Job" aside={isFinished(job.status) ? undefined : "Refreshes every 5s"}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <Fact label="Status">
          <JobStatusPill status={job.status} apiStatus={job.apiStatus} />
        </Fact>
        <Fact label="Workflow">
          {job.workflow.name}
          {job.workflowVersion !== null && (
            <span className="text-ink-3"> · v{job.workflowVersion}</span>
          )}
        </Fact>
        <Fact label="Type">{job.workflow.portal || "—"}</Fact>
        <Fact label="Entity">
          {job.entity.name}
          <span className="text-ink-3"> · {job.entity.full}</span>
        </Fact>
        <Fact label="Run type">
          {job.runMode ?? "—"}
          {job.testMode ? <span className="text-ink-3"> · labelled test</span> : null}
        </Fact>
        <Fact label="Queued by">{job.user?.name ?? "—"}</Fact>
        <Fact label="Queued">{fmtDateTime(job.queuedAt)}</Fact>
        <Fact label="Started">{job.startedAt ? fmtDateTime(job.startedAt) : "Not started"}</Fact>
        <Fact label="Completed">
          {job.completedAt ? fmtDateTime(job.completedAt) : "Not finished"}
        </Fact>
        <Fact label="Duration">
          {duration}
          {/* Still on a runner, including while it waits for a person. */}
          {isOnRunnerStatus(job.apiStatus) && ran ? <span className="text-ink-3"> so far</span> : null}
        </Fact>
        <Fact label="Job ID">
          <span className="font-mono text-xs">{job.id}</span>
        </Fact>
      </dl>
    </Panel>
  );
}
